import { z } from "zod";
import { isoDateSchema as isoDate, reportFilterSchema } from "./report-filter.js";
/**
 * Google Analytics, connected per brand, and the visits AI assistants sent.
 *
 * Every state the screen can be in is a value here, so none of them is a blank
 * box or a zero (AGENTS.md §1): Google sign-in or the sealing key missing on
 * this deployment (`off`), nothing connected yet, the owner still choosing a
 * property, connected, or lost because Google stopped honouring the grant.
 */
/**
 * Core's `AI_ASSISTANTS`, copied: contracts reach the browser, and core holds
 * Node-only modules (`node:crypto`), so importing the list from core would
 * blank every screen — as it did once for site categories. `analytics.test.ts`
 * fails if this copy and core's ever differ.
 */
export const AI_ASSISTANT_KEYS = [
    "chatgpt",
    "gemini",
    "perplexity",
    "copilot",
    "claude",
    "deepseek",
    "grok",
    "meta_ai",
    "other",
];
export const aiAssistantSchema = z.enum(AI_ASSISTANT_KEYS);
const connectedSchema = z.object({
    googleEmail: z.string(),
    property: z.object({ id: z.string(), name: z.string() }),
    /** The days read so far; null until the first read lands. */
    syncedFrom: isoDate.nullable(),
    syncedThrough: isoDate.nullable(),
    /** Why the last read stopped short, as a code; null when it did not. */
    failure: z.string().nullable(),
});
export const analyticsConnectionSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("off"), reason: z.string() }),
    z.object({ state: z.literal("not_connected") }),
    z.object({ state: z.literal("choosing"), googleEmail: z.string() }),
    connectedSchema.extend({ state: z.literal("connected") }),
    connectedSchema.extend({ state: z.literal("lost"), failure: z.string() }),
]);
export const analyticsPropertiesSchema = z.object({
    properties: z.array(z.object({ id: z.string(), name: z.string(), account: z.string() })),
});
export const analyticsChooseSchema = z.object({
    propertyId: z.string().regex(/^properties\/\d+$/u, "a GA4 property, properties/123"),
});
/** The Overview's "Visitors from AI". The engine filter does not apply here. */
export const aiVisitsFilterSchema = reportFilterSchema.pick({ from: true, to: true });
export const aiVisitsSchema = z.discriminatedUnion("state", [
    z.object({
        state: z.literal("unavailable"),
        connection: analyticsConnectionSchema,
    }),
    z.object({
        state: z.literal("read"),
        connection: analyticsConnectionSchema,
        /**
         * The part of the range we hold data for. A range reaching past it is
         * answered for what we have, and the screen says which days that is.
         */
        covered: z.object({ from: isoDate, to: isoDate }).nullable(),
        aiSessions: z.number().int().min(0),
        allSessions: z.number().int().min(0),
        /** Every page an AI visit landed on, not only the ones `pages` lists. */
        landedPages: z.number().int().min(0),
        assistants: z.array(z.object({ assistant: aiAssistantSchema, sessions: z.number().int().min(0) })),
        days: z.array(z.object({
            day: isoDate,
            aiSessions: z.number().int().min(0),
            allSessions: z.number().int().min(0),
        })),
        /**
         * The brand's own pages, by what AI did with them: how often answers in
         * range cited the page (Crux) and how many AI-sent visits landed on it (GA4).
         */
        pages: z.array(z.object({
            path: z.string(),
            citations: z.number().int().min(0),
            sessions: z.number().int().min(0),
        })),
    }),
]);
//# sourceMappingURL=analytics.js.map